import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Grid, MenuItem, Box, Typography, CircularProgress, Avatar,
} from "@mui/material";
import { AssignmentIndOutlined } from "@mui/icons-material";
import { assignThread, fetchTeamUsers, TeamUser, PRIORITY_OPTIONS } from "./emailHubV3Api";

interface Props {
  open: boolean;
  onClose: () => void;
  threadId: number;
  currentAssignee?: number | null;
  currentPriority?: string;
  currentDueAt?: string | null;
  onUpdated: (thread: any) => void;
}

const AssignThreadModal: React.FC<Props> = ({
  open, onClose, threadId, currentAssignee, currentPriority, currentDueAt, onUpdated,
}) => {
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedUser, setSelectedUser] = useState<number | "">("");
  const [priority, setPriority] = useState(currentPriority || "medium");
  const [dueAt, setDueAt] = useState(currentDueAt?.slice(0, 16) || "");

  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchTeamUsers()
        .then(setUsers)
        .catch(() => {})
        .finally(() => setLoading(false));
      setSelectedUser(currentAssignee || "");
      setPriority(currentPriority || "medium");
      setDueAt(currentDueAt?.slice(0, 16) || "");
    }
  }, [open, currentAssignee, currentPriority, currentDueAt]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data: any = {
        assigned_to: selectedUser || null,
        priority,
      };
      if (dueAt) data.due_at = dueAt;
      const updated = await assignThread(threadId, data);
      onUpdated(updated);
      onClose();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
        <AssignmentIndOutlined sx={{ color: "#6366f1" }} /> Assign Thread
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField label="Assign To" value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value as any)}
                fullWidth select size="small">
                <MenuItem value="">Unassigned</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: "#6366f1" }}>
                        {u.username?.[0]?.toUpperCase() || "U"}
                      </Avatar>
                      {u.username}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Priority" value={priority}
                onChange={(e) => setPriority(e.target.value)}
                fullWidth select size="small">
                {PRIORITY_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>
                    <Typography sx={{ textTransform: "capitalize" }}>{p}</Typography>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Due At" value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                fullWidth size="small" type="datetime-local"
                InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <AssignmentIndOutlined />}
          sx={{ background: "#6366f1", "&:hover": { background: "#4f46e5" } }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignThreadModal;
